import React, { forwardRef } from "react";
import { FaCheck } from "react-icons/fa";

export interface StepperProps {
  /** Current active step (0-indexed) */
  activeStep: number;
  /** Array of step configurations */
  steps: StepConfig[];
  /** Orientation */
  orientation?: "horizontal" | "vertical";
  /** Alternative label position (below icon) */
  alternativeLabel?: boolean;
  /** Custom class name */
  className?: string;
  /** Callback when step is clicked */
  onStepClick?: (stepIndex: number) => void;
  /** Allow clicking on completed steps */
  nonLinear?: boolean;
  /** Custom connector component */
  connector?: React.ReactNode;
  /** Size variant */
  size?: "sm" | "md" | "lg";
}

export interface StepConfig {
  /** Step key/id */
  key: string;
  /** Step label/text - supports both 'text' and 'label' for compatibility */
  text?: string;
  /** Step label (alias for text) */
  label?: string;
  /** Optional description */
  description?: string;
  /** Optional icon */
  icon?: React.ReactNode;
  /** Optional error state */
  error?: boolean;
  /** Optional disabled state */
  disabled?: boolean;
  /** Optional completed override */
  completed?: boolean;
}

export interface StepProps {
  /** Step index */
  index: number;
  /** Step configuration */
  step: StepConfig;
  /** Whether this step is active */
  active: boolean;
  /** Whether this step is completed */
  completed: boolean;
  /** Whether this is the last step */
  isLast: boolean;
  /** Orientation */
  orientation: "horizontal" | "vertical";
  /** Alternative label position */
  alternativeLabel: boolean;
  /** Click handler */
  onClick?: () => void;
  /** Size variant */
  size: "sm" | "md" | "lg";
  /** Custom connector */
  connector?: React.ReactNode;
}

// Size configuration
const sizeConfig = {
  sm: {
    icon: "w-6 h-6 text-xs",
    label: "text-xs",
    description: "text-xs",
    connectorHeight: "h-0.5",
    connectorWidth: "w-0.5",
  },
  md: {
    icon: "w-8 h-8 text-sm",
    label: "text-sm",
    description: "text-xs",
    connectorHeight: "h-0.5",
    connectorWidth: "w-0.5",
  },
  lg: {
    icon: "w-10 h-10 text-base",
    label: "text-base",
    description: "text-sm",
    connectorHeight: "h-1",
    connectorWidth: "w-1",
  },
};

/**
 * Get the label text from step config (supports both 'text' and 'label')
 */
const getStepLabel = (step: StepConfig): string => {
  return step.text || step.label || "";
};

/**
 * Individual Step component
 */
const Step: React.FC<StepProps> = ({
  index,
  step,
  active,
  completed,
  isLast,
  orientation,
  alternativeLabel,
  onClick,
  size,
}) => {
  const isClickable = onClick && !step.disabled;
  const isHorizontal = orientation === "horizontal";
  const sizes = sizeConfig[size];
  const labelText = getStepLabel(step);

  // Determine icon/number to display
  const renderIcon = () => {
    if (step.error) {
      return <span className="text-error font-bold">!</span>;
    }
    if (completed) {
      return step.icon || <FaCheck className="w-3 h-3" />;
    }
    if (step.icon) {
      return step.icon;
    }
    return <span className="font-semibold">{index + 1}</span>;
  };

  // Get icon background color
  const getIconStyles = () => {
    if (step.error) {
      return "bg-error-light text-error border-error";
    }
    if (completed) {
      return "bg-accent text-white border-accent";
    }
    if (active) {
      return "bg-accent text-white border-accent shadow-arc-accent";
    }
    return "bg-surface-secondary text-content-tertiary border-arc-border";
  };

  // Get connector color
  const getConnectorColor = () => {
    return completed ? "bg-accent" : "bg-arc-border";
  };

  // Get label color
  const getLabelColor = () => {
    if (active) return "text-accent";
    if (completed) return "text-content-primary";
    return "text-content-tertiary";
  };

  // Horizontal with alternative label (label below icon)
  // This mode is handled differently in the parent Stepper component
  if (isHorizontal && alternativeLabel) {
    return null; // Handled in Stepper component directly
  }

  // Horizontal layout (label beside icon) - DEFAULT
  if (isHorizontal) {
    return (
      <div className="flex items-center flex-1">
        {/* Step content: icon + label */}
        <button
          type="button"
          onClick={isClickable ? onClick : undefined}
          disabled={step.disabled}
          className={`
            flex items-center gap-2 shrink-0
            ${isClickable ? "cursor-pointer" : ""}
            ${step.disabled ? "opacity-50 cursor-not-allowed" : ""}
          `}
        >
          {/* Icon */}
          <div
            className={`
              flex items-center justify-center
              ${sizes.icon}
              rounded-full border-2
              ${getIconStyles()}
              transition-all duration-200
              ${isClickable ? "hover:shadow-arc-accent" : ""}
            `}
          >
            {renderIcon()}
          </div>

          {/* Label */}
          <div>
            <div className={`${sizes.label} font-medium ${getLabelColor()} transition-colors duration-200 whitespace-nowrap`}>
              {labelText}
            </div>
            {step.description && (
              <div className={`${sizes.description} text-content-tertiary`}>
                {step.description}
              </div>
            )}
          </div>
        </button>

        {/* Connector line */}
        {!isLast && (
          <div
            className={`flex-1 mx-4 ${sizes.connectorHeight} ${getConnectorColor()} transition-colors duration-200`}
          />
        )}
      </div>
    );
  }

  // Vertical layout
  return (
    <div className="flex">
      <div className="flex flex-col items-center">
        <button
          type="button"
          onClick={isClickable ? onClick : undefined}
          disabled={step.disabled}
          className={`
            flex items-center justify-center
            ${sizes.icon}
            rounded-full border-2
            ${getIconStyles()}
            transition-all duration-200
            ${isClickable ? "cursor-pointer hover:shadow-arc-accent" : ""}
            ${step.disabled ? "opacity-50 cursor-not-allowed" : ""}
          `}
        >
          {renderIcon()}
        </button>
        {!isLast && (
          <div
            className={`my-1 min-h-[24px] ${sizes.connectorWidth} ${getConnectorColor()} transition-colors duration-200`}
          />
        )}
      </div>
      <div className="ml-3 pb-4">
        <div className={`${sizes.label} font-medium ${getLabelColor()} transition-colors duration-200`}>
          {labelText}
        </div>
        {step.description && (
          <div className={`${sizes.description} text-content-tertiary mt-0.5`}>
            {step.description}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Arc-style Stepper component
 *
 * Features:
 * - Horizontal and vertical orientation
 * - Alternative label position
 * - Custom icons
 * - Error states
 * - Clickable steps (non-linear navigation)
 * - Smooth transitions
 */
const Stepper = forwardRef<HTMLDivElement, StepperProps>(
  (
    {
      activeStep,
      steps,
      orientation = "horizontal",
      alternativeLabel = false,
      className = "",
      onStepClick,
      nonLinear = false,
      connector,
      size = "md",
    },
    ref
  ) => {
    const isHorizontal = orientation === "horizontal";
    const sizes = sizeConfig[size];

    // Helper functions for alternativeLabel mode
    const getIconStyles = (completed: boolean, active: boolean, error?: boolean) => {
      if (error) {
        return "bg-error-light text-error border-error";
      }
      if (completed) {
        return "bg-accent text-white border-accent";
      }
      if (active) {
        return "bg-accent text-white border-accent shadow-arc-accent";
      }
      return "bg-surface-secondary text-content-tertiary border-arc-border";
    };

    const getLabelColor = (completed: boolean, active: boolean) => {
      if (active) return "text-accent";
      if (completed) return "text-content-primary";
      return "text-content-tertiary";
    };

    const getConnectorColor = (completed: boolean) => {
      return completed ? "bg-accent" : "bg-arc-border";
    };

    const renderIcon = (step: StepConfig, index: number, completed: boolean) => {
      if (step.error) {
        return <span className="text-error font-bold">!</span>;
      }
      if (completed) {
        return step.icon || <FaCheck className="w-3 h-3" />;
      }
      if (step.icon) {
        return step.icon;
      }
      return <span className="font-semibold">{index + 1}</span>;
    };

    // Alternative label mode - render everything in parent for proper alignment
    if (isHorizontal && alternativeLabel) {
      // Pre-calculate completion status for connector coloring
      // A connector between step N and step N+1 is colored if step N is completed
      const getConnectorColorBetween = (leftIndex: number) => {
        const leftStep = steps[leftIndex];
        const leftCompleted = leftStep.completed !== undefined ? leftStep.completed : leftIndex < activeStep;
        return leftCompleted ? "bg-accent" : "bg-arc-border";
      };

      return (
        <div
          ref={ref}
          className={`w-full ${className}`}
          role="navigation"
          aria-label="Progress"
        >
          {/* Grid layout for perfect alignment */}
          <div 
            className="grid w-full gap-0"
            style={{ gridTemplateColumns: `repeat(${steps.length}, 1fr)` }}
          >
            {/* Row 1: Icons with connectors */}
            {steps.map((step, index) => {
              const isActive = index === activeStep;
              const isCompleted = step.completed !== undefined ? step.completed : index < activeStep;
              const isLast = index === steps.length - 1;
              const isFirst = index === 0;
              const isClickable = (nonLinear || isCompleted) && !step.disabled && onStepClick;

              // Connector color: based on the step to the LEFT of the connector
              // Left half connector uses previous step's completion status
              const leftConnectorColor = !isFirst ? getConnectorColorBetween(index - 1) : "";
              // Right half connector uses current step's completion status
              const rightConnectorColor = !isLast ? getConnectorColorBetween(index) : "";

              const handleClick = () => {
                if (isClickable) {
                  onStepClick?.(index);
                }
              };

              return (
                <div key={step.key} className="flex items-center">
                  {/* Left connector (half) - colored based on previous step */}
                  {!isFirst && (
                    <div
                      className={`flex-1 ${sizes.connectorHeight} ${leftConnectorColor} transition-colors duration-200`}
                    />
                  )}
                  {isFirst && <div className="flex-1" />}

                  {/* Icon */}
                  <button
                    type="button"
                    onClick={isClickable ? handleClick : undefined}
                    disabled={step.disabled}
                    className={`
                      flex items-center justify-center shrink-0
                      ${sizes.icon}
                      rounded-full border-2
                      ${getIconStyles(isCompleted, isActive, step.error)}
                      transition-all duration-200
                      ${isClickable ? "cursor-pointer hover:shadow-arc-accent" : ""}
                      ${step.disabled ? "opacity-50 cursor-not-allowed" : ""}
                    `}
                  >
                    {renderIcon(step, index, isCompleted)}
                  </button>

                  {/* Right connector (half) - colored based on current step */}
                  {!isLast && (
                    <div
                      className={`flex-1 ${sizes.connectorHeight} ${rightConnectorColor} transition-colors duration-200`}
                    />
                  )}
                  {isLast && <div className="flex-1" />}
                </div>
              );
            })}

            {/* Row 2: Labels - each in its own grid cell, centered */}
            {steps.map((step, index) => {
              const isActive = index === activeStep;
              const isCompleted = step.completed !== undefined ? step.completed : index < activeStep;
              const labelText = getStepLabel(step);

              return (
                <div
                  key={`label-${step.key}`}
                  className="flex flex-col items-center mt-3 px-1"
                >
                  <div className={`${sizes.label} font-medium ${getLabelColor(isCompleted, isActive)} transition-colors duration-200 text-center`}>
                    {labelText}
                  </div>
                  {step.description && (
                    <div className={`${sizes.description} text-content-tertiary mt-0.5 text-center`}>
                      {step.description}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // Default horizontal or vertical mode
    return (
      <div
        ref={ref}
        className={`
          flex
          ${isHorizontal ? "flex-row items-center w-full" : "flex-col"}
          ${className}
        `}
        role="navigation"
        aria-label="Progress"
      >
        {steps.map((step, index) => {
          const isActive = index === activeStep;
          const isCompleted =
            step.completed !== undefined
              ? step.completed
              : index < activeStep;
          const isLast = index === steps.length - 1;

          const handleClick = () => {
            if (nonLinear || isCompleted) {
              onStepClick?.(index);
            }
          };

          return (
            <Step
              key={step.key}
              index={index}
              step={step}
              active={isActive}
              completed={isCompleted}
              isLast={isLast}
              orientation={orientation}
              alternativeLabel={alternativeLabel}
              onClick={onStepClick ? handleClick : undefined}
              size={size}
              connector={connector}
            />
          );
        })}
      </div>
    );
  }
);

Stepper.displayName = "Stepper";

export { Stepper, Step };
export default Stepper;
